import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

interface Props {
  /**
   * Route segment to watch (e.g. `MainRoutes.analytics`). The dialog opens once
   * the user navigates from a path inside this segment to one outside of it.
   */
  route: string;

  /**
   * Renders the dialog to display once the user leaves the route.
   * Receives a `close` callback to dismiss the dialog.
   */
  renderDialog: (close: () => void) => ReactNode;

  /**
   * Optional predicate checked on each route exit to decide whether the dialog
   * may open. Re-evaluated every time so it reflects the latest state (e.g.
   * once feedback has been provided it can stop the dialog from reopening).
   */
  canOpen?: () => boolean;
}

/** Whether the given pathname belongs to the given route segment. */
function isOnRoute(pathname: string, route: string): boolean {
  return pathname.includes(`/${route}`);
}

/**
 * Watches the current location and opens a dialog when the user navigates away
 * from a given sidebar route section (e.g. to gather feedback on exit).
 * Reusable for any sidebar item by passing a different `route` and `renderDialog`.
 */
export function SidebarRouteExitDialog({
  route,
  renderDialog,
  canOpen,
}: Props): ReactNode {
  const { pathname } = useLocation();
  const previousPathnameRef = useRef(pathname);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    const hasLeftRoute =
      isOnRoute(previousPathnameRef.current, route) &&
      !isOnRoute(pathname, route);
    previousPathnameRef.current = pathname;

    if (hasLeftRoute && (canOpen?.() ?? true)) {
      setIsDialogOpen(true);
    }
  }, [pathname, route, canOpen]);

  if (!isDialogOpen) {
    return null;
  }

  return renderDialog(() => setIsDialogOpen(false));
}
